import { ICommitmentAddEditRequest } from '../../../../../model/sge/commitment/request/commitment-add-edit-request';
import { ICommitmentResponse } from '../../../../../model/sge/commitment/response/commitment-response';
import { ILiquidationAddEditRequest } from '../../../../../model/sge/liquidation/request/liquidation-add-edit-request';
import { ILiquidationResponse } from '../../../../../model/sge/liquidation/response/liquidation-response';
import { IPaymentAddEditRequest } from '../../../../../model/sge/payment/request/payment-add-edit-request';
import { IPaymentResponse } from '../../../../../model/sge/payment/response/payment-response';
import { ITransferAddEditRequest } from '../../../../../model/sge/transfer/request/transfer-add-edit-request';
import { ITransferResponse } from '../../../../../model/sge/transfer/response/transfer-response';
import { IAccountReportAddEditRequest } from '../../../../../model/sge/account-report/request/account-report-add-edit-request';
import { IAccountReportResponse } from '../../../../../model/sge/account-report/response/account-report-response';
import { toDateInputValue } from '../../../../../utils/date';
import { ValidationErrors, isBlank, isEmptyId } from '../../../../../utils/validation';

// Formulários dos drawers: todos os campos opcionais enquanto o usuário preenche
export type CommitmentForm = Partial<ICommitmentAddEditRequest>;
export type LiquidationForm = Partial<ILiquidationAddEditRequest>;
export type PaymentForm = Partial<IPaymentAddEditRequest>;
export type TransferForm = Partial<ITransferAddEditRequest>;
export type AccountReportForm = Partial<Omit<IAccountReportAddEditRequest, 'files'>>;

function isMissingAmount(value?: number): boolean {
    return !value || value <= 0;
}

export function sumAmounts<T>(items: T[], getAmount: (item: T) => number | undefined): number {
    return items.reduce((total, item) => total + (getAmount(item) || 0), 0);
}

// Total previsto após salvar: soma dos demais itens + valor digitado (o item em edição é substituído)
export function projectTotal<T extends { id: number }>(items: T[], getAmount: (item: T) => number | undefined, editingId: number | undefined, newAmount?: number): number {
    return sumAmounts(items.filter((item) => item.id !== editingId), getAmount) + (newAmount || 0);
}

export function percentOf(current: number, total: number): number {
    return total ? (current / total) * 100 : 0;
}

// Mapeamento resposta → formulário
export function toCommitmentForm(data?: ICommitmentResponse): CommitmentForm {
    if (!data) return {};
    return {
        commitmentNumber: data.commitmentNumber,
        commitmentDate: toDateInputValue(data.commitmentDate),
        publicationDate: toDateInputValue(data.publicationDate),
        committedAmount: data.committedAmount,
        supplierId: data.supplierId,
        bankAccountUsed: data.bankAccountUsed,
        objectLink: data.objectLink,
    };
}

export function toLiquidationForm(data?: ILiquidationResponse): LiquidationForm {
    if (!data) return {};
    return {
        liquidationNumber: data.liquidationNumber,
        liquidationDate: toDateInputValue(data.liquidationDate),
        liquidatedAmount: data.liquidatedAmount,
        observations: data.observations,
    };
}

export function toPaymentForm(data?: IPaymentResponse): PaymentForm {
    if (!data) return {};
    return { paymentOrderNumber: data.paymentOrderNumber, paymentDate: toDateInputValue(data.paymentDate), paidAmount: data.paidAmount };
}

export function toTransferForm(data?: ITransferResponse): TransferForm {
    if (!data) return {};
    return {
        companyName: data.companyName,
        tradeName: data.tradeName,
        cnpj: data.cnpj,
        legalRepresentativeName: data.legalRepresentativeName,
        legalInstrumentType: data.legalInstrumentType,
        instrumentNumber: data.instrumentNumber,
        signatureDate: toDateInputValue(data.signatureDate),
        effectiveStartDate: toDateInputValue(data.effectiveStartDate),
        effectiveEndDate: toDateInputValue(data.effectiveEndDate),
        transferredAmount: data.transferredAmount,
    };
}

export function toAccountReportForm(data?: IAccountReportResponse): AccountReportForm {
    if (!data) return {};
    return {
        periodStartDate: toDateInputValue(data.periodStartDate),
        periodEndDate: toDateInputValue(data.periodEndDate),
        submissionDate: toDateInputValue(data.submissionDate),
        reviewDate: toDateInputValue(data.reviewDate),
        status: data.status,
        observations: data.observations,
    };
}

// Validações
export function validateCommitment(form: CommitmentForm): ValidationErrors<'commitmentNumber' | 'commitmentDate' | 'publicationDate' | 'supplierId' | 'committedAmount' | 'objectLink'> {
    const errors: ReturnType<typeof validateCommitment> = {};
    if (isBlank(form.commitmentNumber)) errors.commitmentNumber = 'Nº do empenho é obrigatório';
    if (isBlank(form.commitmentDate)) errors.commitmentDate = 'Data do empenho é obrigatória';
    if (isBlank(form.publicationDate)) errors.publicationDate = 'Data de publicação é obrigatória';
    if (isEmptyId(form.supplierId)) errors.supplierId = 'Fornecedor é obrigatório';
    if (isMissingAmount(form.committedAmount)) errors.committedAmount = 'Valor empenhado é obrigatório';
    if (isBlank(form.objectLink)) errors.objectLink = 'Objeto de vínculo é obrigatório';
    return errors;
}

export function validateLiquidation(form: LiquidationForm): ValidationErrors<'liquidationNumber' | 'liquidationDate' | 'liquidatedAmount'> {
    const errors: ReturnType<typeof validateLiquidation> = {};
    if (isBlank(form.liquidationNumber)) errors.liquidationNumber = 'Nº da liquidação é obrigatório';
    if (isBlank(form.liquidationDate)) errors.liquidationDate = 'Data é obrigatória';
    if (isMissingAmount(form.liquidatedAmount)) errors.liquidatedAmount = 'Valor liquidado é obrigatório';
    return errors;
}

export function validatePayment(form: PaymentForm): ValidationErrors<'paymentOrderNumber' | 'paymentDate' | 'paidAmount'> {
    const errors: ReturnType<typeof validatePayment> = {};
    if (isBlank(form.paymentOrderNumber)) errors.paymentOrderNumber = 'Nº da ordem de pagamento é obrigatório';
    if (isBlank(form.paymentDate)) errors.paymentDate = 'Data é obrigatória';
    if (isMissingAmount(form.paidAmount)) errors.paidAmount = 'Valor pago é obrigatório';
    return errors;
}

export function validateTransfer(form: TransferForm): ValidationErrors<'companyName' | 'cnpj' | 'legalInstrumentType' | 'instrumentNumber' | 'signatureDate' | 'effectiveStartDate' | 'effectiveEndDate' | 'transferredAmount'> {
    const errors: ReturnType<typeof validateTransfer> = {};
    if (isBlank(form.companyName)) errors.companyName = 'Razão social é obrigatória';
    if (isBlank(form.cnpj)) errors.cnpj = 'CNPJ é obrigatório';
    if (isEmptyId(form.legalInstrumentType)) errors.legalInstrumentType = 'Tipo de instrumento é obrigatório';
    if (isBlank(form.instrumentNumber)) errors.instrumentNumber = 'Nº do instrumento é obrigatório';
    if (isBlank(form.signatureDate)) errors.signatureDate = 'Data de assinatura é obrigatória';
    if (isBlank(form.effectiveStartDate)) errors.effectiveStartDate = 'Início da vigência é obrigatório';
    if (isBlank(form.effectiveEndDate)) errors.effectiveEndDate = 'Fim da vigência é obrigatório';
    else if (form.effectiveStartDate && form.effectiveEndDate! < form.effectiveStartDate) errors.effectiveEndDate = 'O fim da vigência deve ser posterior ao início';
    if (isMissingAmount(form.transferredAmount)) errors.transferredAmount = 'Valor transferido é obrigatório';
    return errors;
}

export function validateAccountReport(form: AccountReportForm): ValidationErrors<'periodStartDate' | 'periodEndDate'> {
    const errors: ReturnType<typeof validateAccountReport> = {};
    if (isBlank(form.periodStartDate)) errors.periodStartDate = 'Início do período é obrigatório';
    if (isBlank(form.periodEndDate)) errors.periodEndDate = 'Fim do período é obrigatório';
    else if (form.periodStartDate && form.periodEndDate! < form.periodStartDate) errors.periodEndDate = 'O fim do período deve ser posterior ao início';
    return errors;
}
